import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { VariableAutocomplete } from '../VariableAutocomplete';
import { VariablePicker } from '../VariablePicker';

import type {
  AnalyticsNodeMeta,
  AgentNodeMeta,
  ClassifierNodeMeta,
  QualityNodeMeta,
  ReviewNodeMeta,
} from '../VariablePicker';
import type { UpdateCallNodeData } from '@repo/shared-types';

const FIELD_SUGGESTIONS = ['outcome', 'lead_data', 'availability_data', 'shift_data'];

interface UpdateCallConfigProps {
  data: UpdateCallNodeData;
  onChange: (data: UpdateCallNodeData) => void;
  responseFieldsSchema?: Record<string, unknown>;
  reviewNodeMetas?: ReviewNodeMeta[];
  classifierNodeMetas?: ClassifierNodeMeta[];
  qualityNodeMetas?: QualityNodeMeta[];
  agentNodeMetas?: AgentNodeMeta[];
  analyticsNodeMetas?: AnalyticsNodeMeta[];
  triggerType?: 'call' | 'dealerkit' | 'any' | 'scheduled' | 'webhook' | 'event';
}

export const UpdateCallConfig = ({
  data,
  onChange,
  responseFieldsSchema,
  reviewNodeMetas,
  classifierNodeMetas,
  qualityNodeMetas,
  agentNodeMetas,
  analyticsNodeMetas,
  triggerType,
}: UpdateCallConfigProps) => {
  const update = (patch: Partial<UpdateCallNodeData>) => onChange({ ...data, ...patch });

  const updateMapping = (index: number, field: string, value: string) => {
    const mappings = data.mappings.map((m, i) => (i === index ? { ...m, [field]: value } : m));
    update({ mappings });
  };

  const addRow = () => update({ mappings: [...data.mappings, { field: '', value: '' }] });

  const removeRow = (index: number) =>
    update({ mappings: data.mappings.filter((_, i) => i !== index) });

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Label</label>
        <Input
          value={data.label}
          onChange={(e) => update({ label: e.target.value })}
          placeholder="Node label"
        />
      </div>

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">
          Call ID <span className="text-[10px] font-normal">(optional — for webhook triggers)</span>
        </label>
        <VariableAutocomplete
          value={data.callId || ''}
          onChange={(v) => update({ callId: v || undefined })}
          placeholder="{{webhook.body.callId}}"
        />
        <p className="text-muted-foreground text-[10px]">
          Leave blank for call-triggered workflows (auto-detected).
        </p>
      </div>

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">
          Call SID <span className="text-[10px] font-normal">(fallback)</span>
        </label>
        <VariableAutocomplete
          value={data.callSid || ''}
          onChange={(v) => update({ callSid: v || undefined })}
          placeholder="{{webhook.body.callSid}}"
        />
        <p className="text-muted-foreground text-[10px]">
          Used when Call ID doesn&apos;t match a record (e.g. WebRTC sessions).
        </p>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-muted-foreground text-xs font-medium">Field Mappings</label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 gap-1 text-xs"
            onClick={addRow}
          >
            <Plus size={12} />
            Add row
          </Button>
        </div>

        <div className="text-muted-foreground text-[10px]">
          Suggested fields: {FIELD_SUGGESTIONS.join(', ')}
        </div>

        {data.mappings.length === 0 && (
          <p className="text-muted-foreground text-xs italic">
            No mappings yet. Click &quot;Add row&quot;.
          </p>
        )}

        {data.mappings.map((mapping, index) => (
          <div key={index} className="flex items-center gap-1">
            <Input
              value={mapping.field}
              onChange={(e) => updateMapping(index, 'field', e.target.value)}
              placeholder="field"
              className="flex-1"
              list={`field-suggestions-${index}`}
            />
            <datalist id={`field-suggestions-${index}`}>
              {FIELD_SUGGESTIONS.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
            <span className="text-muted-foreground text-xs">→</span>
            <VariableAutocomplete
              value={mapping.value}
              onChange={(v) => updateMapping(index, 'value', v)}
              placeholder="value"
              responseFieldsSchema={responseFieldsSchema}
              reviewNodeMetas={reviewNodeMetas}
              classifierNodeMetas={classifierNodeMetas}
              qualityNodeMetas={qualityNodeMetas}
              agentNodeMetas={agentNodeMetas}
              analyticsNodeMetas={analyticsNodeMetas}
              className="flex-1"
            />
            <VariablePicker
              onSelect={(v) => updateMapping(index, 'value', mapping.value + v)}
              responseFieldsSchema={responseFieldsSchema}
              reviewNodeMetas={reviewNodeMetas}
              classifierNodeMetas={classifierNodeMetas}
              qualityNodeMetas={qualityNodeMetas}
              agentNodeMetas={agentNodeMetas}
              analyticsNodeMetas={analyticsNodeMetas}
              triggerType={triggerType}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive h-8 w-8 p-0"
              onClick={() => removeRow(index)}
            >
              <Trash2 size={14} />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};
